import { Show, createMemo, createSignal } from 'solid-js';
import { createStore } from 'solid-js/store';
import { SelectableObject } from '../interactions/SelectableObject';
import { TextNode } from './BaseTextObject';
import { Placeholder } from '../Placeholder';
import { resolveEffectiveStyle } from '../../project/themeVariables';
import type { ITextImpl } from './AbstractTextImpl';
import { PlainTextImpl } from './PlainTextImpl';
import { TemplateTextImpl } from './TemplateTextImpl';
import { IRenderContext } from 'Shared/posters/canvas/Object';
import { isUserFacing } from 'Shared/posters/project/PosterMode';
import { adoptDesignGeometry, defaultSeedBox, type GeometrySeed, type Dimensions } from 'Shared/posters/canvas/interactions/ObjectImpl';
import { cloneBox, type RuntimeText, type Box } from '../../types';
import { PlainWrap } from 'Shared/posters/canvas/text/PlainWrap';
import { TextEditOverlay } from './TextEditOverlay';
import type { ObjectContext } from '../../project/objects';
import type { SelectionContext } from '../selection';
import type { HistoryContext } from '../../project/history';

type TextKind = 'plain' | 'design' | 'user';


export function TextObject(P: IRenderContext)
{
	const effStyle   = () => resolveEffectiveStyle(P.object, P.theme);
	const lineHeight = () => (effStyle()?.lineHeight ?? 1.2) as number;
	const textAlign  = () => (effStyle()?.textAlign  ?? 'center') as string;

	// Read mode counts as user-facing: the public page renders the same resolved record the preview
	// does, so its fields must wrap their content in full rather than being cropped to the template's
	// designed line count (see isUserFacing).
	const userFacing = () => isUserFacing(P.mode);

	// Whether the shadow template is laying this poster's leaves out — a user-mode field is then laid
	// out at its design width so its container aligns the reserved box, and shrink-wraps back onto its
	// ink to draw (see PlainWrap.layoutSize/displayBox). Reads design.linked() alone: isLinkedLeaf and
	// laidOut both read the layout memo, which reads back through metricsOf → impl.layoutSize(), so
	// consulting them from a width would close a reactive cycle.
	const laidOutByTemplate = () => userFacing() && P.design.linked();

	let contentRef: SVGGElement | undefined;

	const [softWrapOverride, setSoftWrapOverride] = createSignal<number[] | null>(null)
	const [widthOverride, setWidthOverride]       = createSignal<number | null>(null)
	const [wrapOverride, setWrapOverride]         = createSignal<number | null>(null)
	const [resized, setResized]                   = createSignal(false)
	const [frozenFont, setFrozenFont]             = createSignal<number | null>(null)

	const kind = () => textKind(P.object as RuntimeText, userFacing());
	const seed = createMemo((): GeometrySeed => createSeed(kind(),P.object,P.record,P.layoutPos,effStyle,lineHeight));

	// The design geometry this store was last seeded/adopted from — see adoptDesignGeometry. Unlike
	// the image/container seeds, a design-mode field's seed *measures* its text, so its dims move with
	// the font epoch and the record; the diff runs on the design's own stored geometry instead, so
	// only a real design change adopts and a re-measure is left to the fit.
	const adopted: { current: Box | null } = { current: null };
	const designBox = (): Box => ({
		pos:  (P.object as RuntimeText).position ?? defaultSeedBox.pos,
		dims: {
			width:  (P.object as RuntimeText).size?.width  ?? defaultSeedBox.dims.width,
			height: (P.object as RuntimeText).size?.height ?? defaultSeedBox.dims.height,
		},
	});

	const [pos, setPos] = createStore<Box>(cloneBox(seed().box));
	const [contentOffsetY, setContentOffsetY] = createSignal(0);

	const textRef = () => P.textRefs[P.object.id];
	const getContentRef = () => contentRef;

	const templateEditState = { hasBeenEdited: false, pendingResize: null };

	const impl = createMemo((): ITextImpl => {
		const object = P.object as RuntimeText;
		const g = seed();
		let created: ITextImpl;

		switch(kind()) {
			case 'plain':
				created = new PlainTextImpl(object, userFacing, P.canvas, effStyle, lineHeight, textAlign, textRef, getContentRef,
					setSoftWrapOverride, softWrapOverride,
					pos, setPos, g, contentOffsetY, setContentOffsetY);
				break;

			case 'design':
				created = new TemplateTextImpl(object, userFacing, P.canvas, effStyle, lineHeight, textAlign, textRef, getContentRef,
					P.record, false, widthOverride, setWidthOverride, templateEditState,
					pos, setPos, g, contentOffsetY, setContentOffsetY, true);
				break;

			default:
				const template = new TemplateTextImpl(object, userFacing, P.canvas, effStyle, lineHeight, textAlign, textRef, getContentRef,
					P.record, true, widthOverride, setWidthOverride, templateEditState,
					pos, setPos, g, contentOffsetY, setContentOffsetY);

				const cornerBase = { width: null as number | null, font: 24 };
				const userEditState = { hasBeenEdited: false };

				created = new PlainWrap(object, userFacing, P.canvas, effStyle, lineHeight, textAlign, textRef, getContentRef,
					template,
					wrapOverride, setWrapOverride,
					resized, setResized, laidOutByTemplate,
					frozenFont, setFrozenFont, cornerBase, userEditState,
					pos, setPos, g, contentOffsetY, setContentOffsetY);
		}

		adoptDesignGeometry(created, designBox(), g, adopted);
		return created;
	});

	const displayText = () => impl().displayText();

	return (
		<>
			<SelectableObject
				objects={P.objects}
				selection={P.selection}
				history={P.history}
				drag={P.drag}
				impl={impl()}
				canvas={P.canvas}
				design={P.design}
				parentOffset={P.parentOffset}
				parentId={P.parentId}
				mode={P.mode}
			>
				{(dims: () => Dimensions) => (
					<>
						<Show when={displayText() === ''}>
							<Placeholder id={P.object.id} width={dims().width} height={dims().height} />
						</Show>

						<g ref={el => contentRef = el}>
							<TextNode
								objectId={P.object.id}
								textRefs={P.textRefs}
								elW={dims().width}
								elH={dims().height}
								style={effStyle()}
								textContent={displayText}
								lineHeight={lineHeight}
								textAlign={textAlign}
								verticalAlign={() => impl().verticalAlign()}
								yOffset={impl().yOffset()}
								overrideFontSize={() => impl().overrideFontSize()}
								wrappedLines={() => {
									const lines = impl().getWrappedLines(() => dims().width);
									return lines ? lines() : undefined;
								}}
							/>
						</g>
					</>
				)}
			</SelectableObject>

			{/* The text edit overlay lives here (not inside SelectableObject's <g>): it Portals to the
				document body and positions itself from the text element's screen box, so it belongs to the
				text object, not the generic host. Rendered reactively off the selection's editing state. */}
			<Show when={P.selection.editing() == P.object.id}>
				<TextEditOverlay
					textRef={() => impl().getTextEl()}
					svgRef={P.drag.svgRef}
					initialText={impl().initialText()}
					onDone={newText => finishTextEdit(newText, impl(), P.objects, P.selection, P.history)}
				/>
			</Show>
		</>
	)
}

// Commit a text edit: persist the new content, leave the editing state, and re-fit the box around the
// edited text (font shrink for fields, else hug-fit). Called from the edit overlay's onDone (blur /
// Escape / unmount).
function finishTextEdit(newText: string, impl: ITextImpl, objCtx: ObjectContext, selCtx: SelectionContext, histCtx: HistoryContext): void
{
	const impls = selCtx.impls;
	impl.onTextEdit(newText, objCtx, impls, histCtx);
	selCtx.stopEdit();
	impl.refreshContent(objCtx, impls, histCtx);
}

function createSeed(kind,object,record,layoutPos,effStyle,lineHeight)
{
	switch(kind) {
		case 'plain':  return PlainTextImpl.initialSeed(object, layoutPos);
		case 'design': return TemplateTextImpl.initialSeed(object, record, effStyle, lineHeight, layoutPos);
		default:       return PlainWrap.initialSeed(object, layoutPos);
	}
}

function textKind(object: RuntimeText, userFacing: boolean): TextKind
{
	if (!object.style?.isField)
		return 'plain';
	return userFacing ? 'user' : 'design';
}

